<template>
  <div class="search-item-detail">
    <template v-if="detailData">
      <div class="search-item-detail-top">
        <div class="search-item-detail-header">
          <div class="search-item-detail-header-side" @click="cancel">{{ $t('owork_base.cancel') }}</div>
          <div class="search-item-detail-header-middle">{{ $t('owork_base.details') }}</div>
        </div>
        <img class="search-item-detail-img" :src="getIconBySuffix(detailData.filename, detailData.type)" />
        <div class="search-item-detail-content">
          <div class="search-item-detail-content-name">{{ detailData.filename }}</div>
          <div class="search-item-detail-content-param">
            <p>{{ $t('owork_base.time') }}</p>
            <p>{{ dayjs(detailData.lastModified).format('YYYY-MM-DD HH:mm:ss') }}</p>
          </div>
          <div v-if="detailData.type !== SUFFIX_TYPE_FOLDER" class="search-item-detail-content-param">
            <p>{{ $t('owork_base.size') }}</p>
            <p>{{ formatBytes(detailData.size) }}</p>
          </div>
          <div class="search-item-detail-content-param">
            <p>{{ $t('owork_base.path') }}</p>
            <p>{{ getFilePath }}</p>
          </div>
        </div>
      </div>
      <div class="search-item-detail-bottom">
        <span class="shallow-btn" :class="{ isSingle: !openIsShow }" @click="openSearchItemFolder">{{
          $t('owork_base.open_the_folder')
        }}</span>
        <span v-if="openIsShow" class="primary-btn" @click="openSearchItem">{{ $t('owork_base.open') }}</span>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import type { SearchResultListItem } from '@core/search/SearchProvider'
  import { getIconBySuffix, SUFFIX_TYPE_FOLDER } from '../fileutil/FileIconHelper'
  import { formatBytes } from '@/core/Utils'
  import dayjs from 'dayjs'
  import { useOtrack } from '@/core/compositions/Otrack'
  import { TrackEventMap } from '@core/TrackConstants'
  import { OpenableFileList } from './SearchResultsHandle'
  import { INSTANCE as ToastManager } from '@/core/ToastManager'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const { track } = useOtrack()
  const emit = defineEmits(['open-in-folder', 'open', 'cancel'])

  const detailData = ref<SearchResultListItem>()
  const openIsShow = ref<boolean>(false)
  const ACTION_TYPE_OPEN_FILE = 1
  const ACTION_TYPE_OPEN_FOLDER = 2

  const cancel = () => {
    emit('cancel')
  }

  const searchItem = (item: SearchResultListItem | undefined) => {
    detailData.value = item
    if (!item) return

    openIsShow.value = OpenableFileList.includes(item.type)
  }

  const openSearchItem = () => {
    track(
      {
        eventId: TrackEventMap.SEARCH_FILE_OPERATION,
      },
      {
        action: ACTION_TYPE_OPEN_FILE,
      }
    )

    if (detailData.value?.size === 0) {
      ToastManager.show('owork_base.prompt_disable_open')

      return
    }
    emit('open', detailData.value)
  }

  const openSearchItemFolder = () => {
    track(
      {
        eventId: TrackEventMap.SEARCH_FILE_OPERATION,
      },
      {
        action: ACTION_TYPE_OPEN_FOLDER,
      }
    )
    emit('open-in-folder', detailData.value)
  }

  // storageType = 0为设备存储，= 1为sd卡，= -1，未知存储类型，这时relativePath = absolutePath
  const enum FilePathStorageType {
    UNKNOWN = -1,
    DEVICE = 0,
    SD = 1,
  }

  const RelativePathList = [FilePathStorageType.DEVICE, FilePathStorageType.SD]

  // 后续按产品要求修改值
  const FilePathStorageTypeMap = new Map<FilePathStorageType, string>([
    [FilePathStorageType.UNKNOWN, 'owork_base.unknown'],
    [FilePathStorageType.DEVICE, 'owork_base.device_storage'],
    [FilePathStorageType.SD, 'owork_base.sd_card'],
  ])

  const getFilePath = computed(() => {
    let path = ''
    const reg = /^\/storage\/emulated\/\d+/i

    if (
      detailData.value?.relativePath &&
      detailData.value?.storageType &&
      RelativePathList.includes(detailData.value?.storageType)
    ) {
      path = detailData.value?.relativePath
    } else {
      path = detailData.value?.absolutePath.replace(reg, '') ?? ''
    }

    return Array.of<string | undefined>(
      t(FilePathStorageTypeMap.get(detailData.value?.storageType || FilePathStorageType.DEVICE)!),
      path
    ).join('')
  })

  defineExpose({ searchItem })
</script>

<style scoped lang="stylus">

  ::-webkit-scrollbar-track
    background #fafafa
  ::-webkit-scrollbar-thumb
    background #e5e5e5
    border-radius 4px
  .search-item-detail
    font-weight default_font_weight
    background-color #fff
    rect(100%)
    display flex
    flex-direction column
    justify-content space-between
    .search-item-detail-top
      width 100%
      text-align center
      .search-item-detail-img
        margin-top 71px
        rect(96px)
        margin-bottom 8px
      .search-item-detail-content
        box-sizing border-box
        padding 0px 32px
        font-size 12px
        max-height 220px
        overflow-y auto
        line-height 16px
        color default_shallow_color
        .search-item-detail-content-name
          margin-bottom 16px
          font-size 14px
          line-height 20px
          min-height 60px
          color #000000
          font-weight bolder_font_weight
          word-break break-all
        .search-item-detail-content-param
          text-align left
          display flex
          align-items baseline
          align-content flex-start
          color  default_shallow_color
          & > p
            word-break break-all
            margin-bottom 8px
          & > p:first-child
            white-space nowrap
          & > p:last-child
            color default_color
    .search-item-detail-bottom
      margin 0 41px 41px
      flex-all-center()
      & > span:nth-child(2)
        margin-left 12px
      .shallow-btn
        shallow-btn(8px 0, 124px, 8px)
      .primary-btn
        primary-btn(8px 0, 120px, 8px)

  @import '../styles/h5Adaptive/SearchInputComponent.styl'
</style>
